import { stringifyLosslessJson } from '@pillar-portal/api-types';
import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { MainLayout } from '@/components/layout/MainLayout';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { StatusBadge } from '@/components/ui/status-badge';
import { ApiError, customerApi } from '@/lib/pillarApi';
import type { MerchantCustomerRecordDetailsResponse } from '@pillar-portal/api-types';

function CustomerDetailPageContent() {
    const params = useParams();
    const merchantCustomerRecordUuid = params.uuid;

    if (
        merchantCustomerRecordUuid === undefined ||
        merchantCustomerRecordUuid.trim().length === 0
    ) {
        throw new Error('Missing merchant customer record UUID');
    }

    const customerQuery = useQuery<MerchantCustomerRecordDetailsResponse>({
        queryKey: ['merchantCustomerDetails', merchantCustomerRecordUuid],
        queryFn: async () => await customerApi.getCustomer(merchantCustomerRecordUuid),
        retry: false,
    });

    if (customerQuery.isLoading) {
        return <div>Loading customer details...</div>;
    }

    if (customerQuery.error !== null || customerQuery.data === undefined) {
        const errorMessage =
            customerQuery.error instanceof ApiError
                ? customerQuery.error.message
                : 'Failed to load customer details.';
        return (
            <Alert variant="destructive">
                <AlertTitle>Unable to load customer</AlertTitle>
                <AlertDescription>{errorMessage}</AlertDescription>
            </Alert>
        );
    }

    const customer = customerQuery.data;
    const canDelete: boolean = customer.status === 'ACTIVE';
    const canUndelete: boolean = customer.status === 'DELETED';

    return (
        <div className="space-y-6">
            <div className="space-y-1">
                <div className="flex items-center gap-3">
                    <h1 className="text-3xl font-bold">{customer.name}</h1>
                    <StatusBadge status={customer.status} />
                </div>
                <p className="text-sm text-muted-foreground">Merchant customer record details</p>
            </div>

            <Card>
                <CardHeader>
                    <CardTitle>Customer Record</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="space-y-2">
                        <Label htmlFor="customer-name">Name</Label>
                        <Input id="customer-name" value={customer.name} disabled readOnly />
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="customer-primary-email">Primary Email</Label>
                        <Input
                            id="customer-primary-email"
                            value={customer.primary_email ?? ''}
                            disabled
                            readOnly
                        />
                    </div>

                    <div className="grid gap-4 md:grid-cols-2">
                        <div className="space-y-1">
                            <p className="text-sm font-medium text-muted-foreground">
                                Primary Phone
                            </p>
                            <p className="text-sm">{customer.primary_phone ?? '—'}</p>
                        </div>
                        <div className="space-y-1">
                            <p className="text-sm font-medium text-muted-foreground">
                                Customer Organization
                            </p>
                            <p className="text-sm">{customer.customer_organization_name ?? '—'}</p>
                        </div>
                    </div>

                    <div className="flex justify-end gap-2">
                        {canDelete ? (
                            <Button variant="destructive" disabled>
                                Delete
                            </Button>
                        ) : null}
                        {canUndelete ? <Button disabled>Undelete</Button> : null}
                        <Button disabled>Save</Button>
                    </div>
                </CardContent>
            </Card>

            <Card>
                <CardHeader>
                    <CardTitle>Customer Details Payload</CardTitle>
                </CardHeader>
                <CardContent>
                    <pre className="overflow-x-auto rounded-md bg-muted p-4 text-sm">
                        {stringifyLosslessJson(customer.customer_details, null, 2)}
                    </pre>
                </CardContent>
            </Card>
        </div>
    );
}

export default function CustomerDetailPage() {
    return (
        <ProtectedRoute>
            <MainLayout>
                <CustomerDetailPageContent />
            </MainLayout>
        </ProtectedRoute>
    );
}
